import React, { useMemo } from "react";
import { Text, Linking, StyleSheet, Platform } from "react-native";

// Simplified URL matcher; will grab everything until whitespace. We'll trim common trailing punctuation or unmatched closing delimiters after.
const URL_REGEX = /((?:https?:\/\/|www\.)\S+)/gi;

const normalizeUrl = (raw) => {
  if (/^www\./i.test(raw)) {
    return `https://${raw}`;
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
    return raw;
  }
  return `https://${raw}`;
};

// Trim trailing punctuation like .,;:!? and unmatched closing delimiters such as ) ] } if they are unbalanced.
const trimTrailingPunctuation = (raw) => {
  let url = raw;
  let trailing = "";

  // 1. Strip common trailing punctuation first (.,;:!?)
  const punctRe = /[.,;:!?]+$/;
  const m1 = url.match(punctRe);
  if (m1) {
    trailing = m1[0] + trailing;
    url = url.slice(0, -m1[0].length);
  }

  // 2. Handle unmatched closing parentheses ')'
  const openPar = (url.match(/\(/g) || []).length;
  const closePar = (url.match(/\)/g) || []).length;
  if (closePar > openPar && url.endsWith(")")) {
    url = url.slice(0, -1);
    trailing = ")" + trailing;
  }

  // 3. Unmatched closing square bracket ']'
  const openSq = (url.match(/\[/g) || []).length;
  const closeSq = (url.match(/\]/g) || []).length;
  if (closeSq > openSq && url.endsWith("]")) {
    url = url.slice(0, -1);
    trailing = "]" + trailing;
  }

  // 4. Unmatched closing curly '}'
  const openCur = (url.match(/{/g) || []).length;
  const closeCur = (url.match(/}/g) || []).length;
  if (closeCur > openCur && url.endsWith("}")) {
    url = url.slice(0, -1);
    trailing = "}" + trailing;
  }

  return { trimmed: url, trailing };
};

const LinkableText = ({
  children,
  style,
  linkStyle,
  numberOfLines,
  ellipsizeMode,
  darkBackground = false,
  ...rest
}) => {
  if (typeof children !== "string") {
    return (
      <Text
        style={[styles.body, style]}
        numberOfLines={numberOfLines}
        ellipsizeMode={ellipsizeMode}
        {...rest}
      >
        {children}
      </Text>
    );
  }

  const text = children;

  const parts = useMemo(() => {
    const segments = [];
    let lastIndex = 0;
    let match;

    while ((match = URL_REGEX.exec(text)) !== null) {
      const raw = match[0];
      const start = match.index;
      const end = URL_REGEX.lastIndex;

      if (lastIndex < start) {
        segments.push({ text: text.slice(lastIndex, start), isLink: false });
      }

      const { trimmed, trailing } = trimTrailingPunctuation(raw);
      segments.push({ text: trimmed, isLink: true });

      if (trailing) {
        segments.push({ text: trailing, isLink: false });
      }

      lastIndex = end;
    }

    if (lastIndex < text.length) {
      segments.push({ text: text.slice(lastIndex), isLink: false });
    }

    return segments.length ? segments : [{ text, isLink: false }];
  }, [text]);

  const handlePress = async (rawUrl) => {
    const url = normalizeUrl(rawUrl);
    try {
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.open(url, "_blank", "noopener");
        return;
      }

      const can = await Linking.canOpenURL(url);
      if (can) {
        await Linking.openURL(url);
      } else {
        await Linking.openURL(url);
      }
    } catch {
      console.warn("Failed to open URL:", url);
    }
  };

  return (
    <Text
      style={[styles.body, style]}
      numberOfLines={numberOfLines}
      ellipsizeMode={ellipsizeMode}
      {...rest}
    >
      {parts.map((seg, i) =>
        seg.isLink ? (
          <Text
            key={`link-${i}`}
            style={[
              styles.link,
              linkStyle,
              { color: darkBackground ? "#abbbe7ff" : "#1a0dab" },
            ]}
            onPress={() => handlePress(seg.text)}
            accessibilityRole="link"
          >
            {seg.text}
          </Text>
        ) : (
          <Text key={`text-${i}`}>{seg.text}</Text>
        )
      )}
    </Text>
  );
};

const styles = StyleSheet.create({
  body: {
    // fontSize: 16,
    // lineHeight: 22,
  },
  link: {
    textDecorationLine: "underline",
  },
});

export default LinkableText;
